/**
 * Standard drill size tables. These are reference data, not derived values —
 * they are transcribed from the standard series and locked down by tests.
 *
 * Three series live in the same rack in every shop:
 *  - fractional, 1/64" steps
 *  - number drills #1 (0.2280") down to #80 (0.0135")
 *  - letter drills A (0.234") up to Z (0.413")
 */

export type DrillSeries = 'fraction' | 'number' | 'letter';

export interface DrillSize {
  readonly label: string;
  readonly inches: number;
  readonly series: DrillSeries;
}

/** #1–#80, in the standard order. Index 0 is #1. */
const NUMBER_DRILLS: readonly number[] = [
  0.228, 0.221, 0.213, 0.209, 0.2055, 0.204, 0.201, 0.199, 0.196, 0.1935,
  0.191, 0.189, 0.185, 0.182, 0.18, 0.177, 0.173, 0.1695, 0.166, 0.161,
  0.159, 0.157, 0.154, 0.152, 0.1495, 0.147, 0.144, 0.1405, 0.136, 0.1285,
  0.12, 0.116, 0.113, 0.111, 0.11, 0.1065, 0.104, 0.1015, 0.0995, 0.098,
  0.096, 0.0935, 0.089, 0.086, 0.082, 0.081, 0.0785, 0.076, 0.073, 0.07,
  0.067, 0.0635, 0.0595, 0.055, 0.052, 0.0465, 0.043, 0.042, 0.041, 0.04,
  0.039, 0.038, 0.037, 0.036, 0.035, 0.033, 0.032, 0.031, 0.0292, 0.028,
  0.026, 0.025, 0.024, 0.0225, 0.021, 0.02, 0.018, 0.016, 0.0145, 0.0135,
];

/** A–Z. */
const LETTER_DRILLS: readonly number[] = [
  0.234, 0.238, 0.242, 0.246, 0.25, 0.257, 0.261, 0.266, 0.272, 0.277,
  0.281, 0.29, 0.295, 0.302, 0.316, 0.323, 0.332, 0.339, 0.348, 0.358,
  0.368, 0.377, 0.386, 0.397, 0.404, 0.413,
];

function greatestCommonDivisor(a: number, b: number): number {
  return b === 0 ? a : greatestCommonDivisor(b, a % b);
}

function buildFractions(): DrillSize[] {
  const out: DrillSize[] = [];
  for (let sixtyFourths = 1; sixtyFourths <= 64; sixtyFourths++) {
    const g = greatestCommonDivisor(sixtyFourths, 64);
    const num = sixtyFourths / g;
    const den = 64 / g;
    out.push({
      label: den === 1 ? `${num}"` : `${num}/${den}"`,
      inches: sixtyFourths / 64,
      series: 'fraction',
    });
  }
  return out;
}

export const FRACTION_DRILLS: readonly DrillSize[] = buildFractions();

export const DRILL_TABLE: readonly DrillSize[] = [
  ...FRACTION_DRILLS,
  ...NUMBER_DRILLS.map((inches, i) => ({
    label: `#${i + 1}`,
    inches,
    series: 'number' as const,
  })),
  ...LETTER_DRILLS.map((inches, i) => ({
    label: String.fromCharCode(65 + i),
    inches,
    series: 'letter' as const,
  })),
];

export interface DrillMatch {
  readonly drill: DrillSize;
  /** Signed: positive means the drill is larger than the target. */
  readonly difference: number;
}

/**
 * Closest drill in the rack to a target diameter.
 *
 * `series` narrows the search — asking for the nearest *fractional* drill is a
 * different (and much more common) question than asking for the nearest drill
 * of any kind.
 */
export function nearestDrill(
  targetInches: number,
  series?: DrillSeries | readonly DrillSeries[],
): DrillMatch {
  const allowed =
    series === undefined
      ? null
      : new Set(Array.isArray(series) ? series : [series as DrillSeries]);

  let best: DrillMatch | null = null;
  for (const drill of DRILL_TABLE) {
    if (allowed && !allowed.has(drill.series)) continue;
    const difference = drill.inches - targetInches;
    if (!best || Math.abs(difference) < Math.abs(best.difference)) {
      best = { drill, difference };
    }
  }
  if (!best) throw new Error(`No drill sizes match series ${String(series)}`);
  return best;
}

/**
 * Smallest drill that is at least the target — what you reach for when the
 * hole has to clear something and undersize is not an option.
 */
export function nextDrillUp(
  targetInches: number,
  series?: DrillSeries | readonly DrillSeries[],
): DrillMatch | null {
  const allowed =
    series === undefined
      ? null
      : new Set(Array.isArray(series) ? series : [series as DrillSeries]);

  let best: DrillMatch | null = null;
  for (const drill of DRILL_TABLE) {
    if (allowed && !allowed.has(drill.series)) continue;
    const difference = drill.inches - targetInches;
    if (difference < -1e-9) continue;
    if (!best || difference < best.difference) best = { drill, difference };
  }
  return best;
}
